import { Button } from "components/DesignSystem/Button/Button";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { SwitchField } from "components/DesignSystem/Switch/SwitchField";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import {
  AdminCoupon,
  useCreateAdminCouponMutation,
  useUpdateAdminCouponMutation,
} from "store/apis/couponAdmin";
import * as yup from "yup";
import {
  getErrorMessage,
  getOneYearFromTodayInputValue,
  getTodayInputValue,
  toDateInputValue,
  toEndOfDayIso,
  toStartOfDayIso,
} from "./couponUtils";

// Prefilled only when creating; the API has no default
const DEFAULT_MAX_DISCOUNT = 1000;

type FormValues = {
  coupon_code: string;
  description: string;
  valid_from: string;
  valid_till: string;
  max_discount: number | "";
  is_free_task_coupon: boolean;
};

const validationSchema = yup.object({
  coupon_code: yup
    .string()
    .trim()
    .required("Coupon code is required")
    .max(10, "Coupon code can be at most 10 characters"),
  valid_from: yup.string().required("Valid from is required"),
  valid_till: yup
    .string()
    .required("Valid till is required")
    .test(
      "after-valid-from",
      "Valid till must be after valid from",
      (validTill, { parent }) =>
        !validTill || !parent.valid_from || validTill >= parent.valid_from
    ),
  max_discount: yup
    .number()
    .typeError("Max discount is required")
    .required("Max discount is required")
    .min(0, "Max discount can't be negative"),
});

type CouponFormModalProps = {
  isOpen: boolean;
  close: () => void;
  // Edit mode when a coupon is passed
  coupon?: AdminCoupon;
};

export const CouponFormModal = ({
  isOpen,
  close,
  coupon,
}: CouponFormModalProps) => {
  const history = useHistory();
  const { alertToast, successToast } = useToast();
  const [createCoupon, { isLoading: isCreating }] =
    useCreateAdminCouponMutation();
  const [updateCoupon, { isLoading: isUpdating }] =
    useUpdateAdminCouponMutation();
  const isEdit = Boolean(coupon);

  const initialValues: FormValues = {
    coupon_code: coupon?.coupon_code ?? "",
    description: coupon?.description ?? "",
    valid_from: coupon
      ? toDateInputValue(coupon.valid_from)
      : getTodayInputValue(),
    valid_till: coupon
      ? toDateInputValue(coupon.valid_till)
      : getOneYearFromTodayInputValue(),
    max_discount: coupon?.max_discount ?? DEFAULT_MAX_DISCOUNT,
    is_free_task_coupon: coupon?.is_free_task_coupon ?? false,
  };

  const onSubmit = async (values: FormValues) => {
    const payload = {
      description: values.description,
      valid_from: toStartOfDayIso(values.valid_from),
      valid_till: toEndOfDayIso(values.valid_till),
      max_discount: Number(values.max_discount),
      is_free_task_coupon: values.is_free_task_coupon,
    };
    try {
      if (coupon) {
        await updateCoupon({ couponId: coupon.uuid, payload }).unwrap();
        successToast({ message: "Coupon updated" });
        close();
        return;
      }
      const created = await createCoupon({
        ...payload,
        coupon_code: values.coupon_code.trim(),
      }).unwrap();
      close();
      history.push(`/admin/coupons/${created.uuid}`);
    } catch (err) {
      alertToast({ message: getErrorMessage(err) });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild>
        <FormikForm<FormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {() => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {isEdit ? `Edit ${coupon?.coupon_code}` : "Create coupon"}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <Input
                  withForm
                  name="coupon_code"
                  label="Coupon code"
                  required
                  disabled={isEdit}
                  placeholder="e.g. BKLYSD"
                />
                <TextArea
                  name="description"
                  label="Description"
                  placeholder="Who is this coupon for?"
                />
                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <DateField name="valid_from" label="Valid from" required />
                  <DateField name="valid_till" label="Valid till" required />
                </div>
                <Input
                  withForm
                  name="max_discount"
                  type="number"
                  min={0}
                  label="Max discount ($)"
                  required
                  placeholder="Total discount balance per group"
                />
                <SwitchField
                  name="is_free_task_coupon"
                  label="Free task coupon"
                  description="Unlocks one free task. Also mark one category mapping as the free task, at 100% off."
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isCreating || isUpdating}
                  disabled={isCreating || isUpdating}
                >
                  {isEdit ? "Save" : "Create"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
